import { LitElement, html } from 'lit';
import { customElement, query, state } from 'lit/decorators.js';

import { emit } from '@amityeko/pbc-core/dist/utilities/event';

import microphoneIcon from '@amityeko/design-system/dist/assets/icons/microphone';
import stopIcon from '@amityeko/design-system/dist/assets/icons/stop';

import '@amityeko/design-system/dist/elements/dialog';
import '@amityeko/design-system/dist/elements/typography/tg-title';
import '@amityeko/design-system/dist/elements/toast';

import type AmityekoDialog from '@amityeko/design-system/dist/elements/dialog';
import type AmityekoToast from '@amityeko/design-system/dist/elements/toast';

import styles from './styles';

/**
 * @dependency amityeko-dialog
 * @dependency amityeko-tg-title
 * @dependency amityeko-toast
 *
 */

@customElement('rnr-record-audio')
export default class RnRRecordAudio extends LitElement {
  static styles = styles;

  @query('#dialog') dialog!: AmityekoDialog;
  @query('#canvas') canvas!: HTMLCanvasElement;
  @query('#stop') stop!: HTMLElement;
  @query('#initial_wave') initialWave!: HTMLElement;
  @query('#toast') toast!: AmityekoToast;

  @state()
  isRecording: boolean = false;

  @state()
  toastMessage: string = '';

  @state()
  mediaRecorder!: MediaRecorder;

  @state()
  tracks: MediaStreamTrack[] = [];

  ctx: any;

  bars: any = [];

  timeOffset: number = 100;

  now: number = Math.floor(performance.now()) / this.timeOffset;

  width: number = 370;

  height: number = 40;

  initWavePosition: number = 0;

  show() {
    this.dialog.show();
  }

  private _showToast(message: string) {
    this.toastMessage = message;
    this.toast.show();
  }

  private _onClick() {
    this.dialog.show();
  }

  private _initializeCanvas() {
    //initialize canvas with 2D
    this.ctx = this.canvas.getContext('2d');
    //add width and height to canvas
    this.canvas.width = this.width;
    this.canvas.height = this.height;
    this.canvas.style.width = this.width + 'px';
    this.canvas.style.height = this.height + 'px';
  }

  private _loop(analyser: any, frequencyArray: any) {
    if (!this.isRecording) return;

    // Increment the waveform position
    this.initWavePosition += 2;
    // Clear the canvas
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

    // Initialize the maximum frequency value
    let max = 0;

    // Check if it is time to update the frequency data
    if (Math.floor(performance.now() / this.timeOffset) > this.now) {
      // Update the current time
      this.now = Math.floor(performance.now() / this.timeOffset);
      // Get the current frequency data from the analyser node
      analyser.getFloatTimeDomainData(frequencyArray);

      // Find the maximum frequency value
      for (var i = 0; i < frequencyArray.length; i++) {
        if (frequencyArray[i] > max) {
          max = frequencyArray[i];
        }
      }

      // Calculate the frequency based on the maximum frequency value
      var freq = Math.floor(max * 650);

      // Add a new bar to the waveform data with the calculated frequency
      this.bars.push({
        x: this.width,
        y: this.height / 2 - freq / 2,
        height: freq || 1,
        width: 3,
      });
    }

    // Draw the waveform on the canvas
    this._drawWave();

    // Set a timeout to call the loop function again after a short delay
    setTimeout(() => {
      // Use requestAnimationFrame to optimize the loop function for better performance
      requestAnimationFrame(() => this._loop(analyser, frequencyArray));
      // Update the position of the initial waveform element
      this.initialWave.style.transform = `translateX(-${this.initWavePosition}px)`;
    }, 27);
  }

  private _drawWave() {
    // Iterate over the waveform data
    for (let i = 0; i < this.bars.length; i++) {
      // Get the current bar data
      const bar = this.bars[i];
      // Set the fill style for the bar
      this.ctx.fillStyle = 'rgb(97, 97, 97)';
      // Draw the bar on the canvas
      this.ctx.fillRect(bar.x, bar.y, bar.width, bar.height);
      // Decrement the x position of the bar
      bar.x = bar.x - 2;
      // If the x position of the bar is less than 1, remove it from the array
      if (bar.x < 1) {
        this.bars.splice(i, 1);
      }
    }
  }

  private _onSuccessAudio(stream: any) {
    this.tracks = stream.getTracks();
    //create a new audio stream
    this.mediaRecorder = new MediaRecorder(stream);

    //start recording audio
    this.mediaRecorder.start();

    //create empty array for storing recorded audio data
    let dataArray: any = [];

    //add recorded data to array
    this.mediaRecorder.ondataavailable = (e: any) => {
      dataArray.push(e.data);
    };

    //When recording is stopped, create blob audio data with type mp3 and emit event
    this.mediaRecorder.onstop = (e: any) => {
      const audioData = new Blob(dataArray, { type: 'audio/mp3;' });
      dataArray = [];

      emit(this, 'rnr-stop-audio-record', { detail: { audio: audioData } });
    };

    //Create Audio Context
    //@ts-ignore
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    const audioContent = new AudioContext();
    const streamSource = audioContent.createMediaStreamSource(stream);

    //create analyser to get audio frequency and domain data
    const analyser = audioContent?.createAnalyser();
    //connect analyser to stream source
    streamSource.connect(analyser);
    /**
     * set fftSize to 512
     * fftSize can be 512, 1024 or 2048
     * A fftSize of 1024 or 2048 will result in more detailed frequency data,
     * however the analysis will also be slower with a larger fftSize.
     */
    analyser.fftSize = 512;
    //create frequency array
    const frequencyArray = new Float32Array(analyser.fftSize);
    this._initializeCanvas();
    this._loop(analyser, frequencyArray);
  }

  private _onFailAudio(error: any) {
    if (error) this._showToast('Something went wrong while recording audio');
    this.isRecording = false;
  }

  private _onStartRecording() {
    this.isRecording = true;
    // set audio true for audio recording
    const options = {
      audio: true,
    };

    /**
     * get the user media from navigator
     * if success, call successCallback with stream
     * if failure, call failureCallback
     */
    navigator.mediaDevices
      .getUserMedia(options)
      .then(stream => {
        this._onSuccessAudio(stream);
      })
      .catch(error => this._onFailAudio(error));
  }

  private _onStopRecording() {
    if (this.mediaRecorder?.state === 'inactive') return;
    this.isRecording = false;
    this.mediaRecorder.stop();
    this.tracks.forEach(track => track.stop());
    this.dialog.hide();
  }

  private _onDialogBack() {
    this.dialog.hide();
    this._onStopRecording();
    emit(this, 'rnr-record-audio-dialog-back');
  }

  protected render() {
    return html`
      <slot @click=${this._onClick}></slot>

      <amityeko-toast
        id="toast"
        variant="error"
        .message=${this.toastMessage}
        .duration=${5000}
      ></amityeko-toast>

      <amityeko-dialog
        id="dialog"
        label="Record Audio"
        ?hasBackButton=${true}
        @amityeko-back=${this._onDialogBack}
        @amityeko-cancel=${this._onStopRecording}
      >
        <div class="content">
          <amityeko-tg-title
            >Tell us what did your teammate do to deserve a recognition?</amityeko-tg-title
          >
          <div class="wave__container">
            <div id="initial_wave" class="initial__wave"></div>
            <canvas id="canvas"></canvas>
          </div>
          <div class="control">
            ${this.isRecording
              ? html`
                  <div class="icon__wrapper">
                    <amityeko-icon id="stop" color="#FB0404" @click=${this._onStopRecording}
                      >${stopIcon}</amityeko-icon
                    >
                  </div>
                `
              : html`
                  <div class="icon__wrapper">
                    <amityeko-icon color="#FB0404" @click=${this._onStartRecording}
                      >${microphoneIcon}</amityeko-icon
                    >
                  </div>
                `}
          </div>
        </div>
      </amityeko-dialog>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'rnr-record-audio': RnRRecordAudio;
  }
}
